import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getUiLang } from '../utils/lang';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Trash2, Plus, Minus, ShoppingBag, ArrowLeft, Tag, X } from 'lucide-react';
import TrustBadges from '../components/TrustBadges';

const CartPage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const {
    cart, currency, updateQuantity, removeFromCart, clearCart,
    getSubtotal, getCouponDiscount, getTotal, getBulkDiscount, getLineTotal,
    coupon, applyCoupon, removeCoupon,
  } = useCart();

  const [couponInput, setCouponInput] = useState('');
  const [couponFeedback, setCouponFeedback] = useState(null); // {type, message}
  const [applying, setApplying] = useState(false);

  const handleApply = async (e) => {
    e.preventDefault();
    if (!couponInput.trim()) return;
    setApplying(true);
    setCouponFeedback(null);
    const res = await applyCoupon(couponInput, user?.email);
    if (res.ok) {
      setCouponFeedback({ type: 'success', message: t('cart.coupon_success', { percent: res.data.discount_percent }) });
      setCouponInput('');
    } else {
      const reasonKey = {
        empty_code: 'cart.coupon_empty',
        not_found: 'cart.coupon_not_found',
        expired: 'cart.coupon_expired',
        already_used: 'cart.coupon_used',
        email_mismatch: 'cart.coupon_email_mismatch',
        product_not_in_cart: 'cart.coupon_product_missing',
        min_not_reached: 'cart.coupon_min_not_reached',
        network_error: 'cart.coupon_network_error',
      }[res.reason] || 'cart.coupon_invalid';
      const message = res.reason === 'min_not_reached' && res.min_order_total
        ? t('cart.coupon_min_not_reached', { min: Math.round(res.min_order_total) })
        : t(reasonKey);
      setCouponFeedback({ type: 'error', message });
    }
    setApplying(false);
  };

  const getTranslation = (obj, field) => {
    return obj?.translations?.[getUiLang(i18n)]?.[field] || obj?.translations?.['sk']?.[field] || '';
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ShoppingBag className="h-24 w-24 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-4" data-testid="cart-empty">{t('cart.empty')}</h2>
          <Link
            to="/catalog"
            className="inline-flex items-center space-x-2 px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition"
            data-testid="continue-shopping"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>{t('cart.continue_shopping')}</span>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-orange-500 to-yellow-400 text-white py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold" data-testid="cart-title">{t('cart.title')}</h1>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md">
              {cart.map((item) => {
                const price = currency === 'EUR' ? item.price_eur : item.price_usd;
                const lineCalc = getLineTotal(item);
                const itemTotal = lineCalc.final;
                const itemDiscount = lineCalc.discount;
                const unit = item.unit || 'mb';
                const isMb = unit === 'mb';
                const step = Number(item.min_order_step_m || (isMb ? 5 : 1));
                const unitLabel = isMb ? t('product.unit_mb', 'bm') : t('product.pcs', 'ks');

                return (
                  <div
                    key={item.id}
                    className="flex flex-col md:flex-row items-center gap-4 p-6 border-b border-gray-200 last:border-b-0"
                    data-testid={`cart-item-${item.sku}`}
                  >
                    {/* Product Image */}
                    <Link to={`/product/${item.id}`} className="flex-shrink-0">
                      <img
                        src={item.images[0]}
                        alt={getTranslation(item, 'name')}
                        className="w-24 h-24 object-cover rounded-lg"
                      />
                    </Link>

                    {/* Product Info */}
                    <div className="flex-1">
                      <Link
                        to={`/product/${item.id}`}
                        className="text-lg font-semibold hover:text-orange-500"
                      >
                        {getTranslation(item, 'name')}
                      </Link>
                      <p className="text-sm text-gray-600 mt-1">
                        {getTranslation(item, 'description').substring(0, 100)}...
                      </p>
                      <div className="mt-2">
                        <span className="text-lg font-bold text-orange-500">
                          {price.toFixed(2)} {currency}
                        </span>
                        <span className="text-sm text-gray-500 ml-1">/ {unitLabel}</span>
                      </div>
                    </div>

                    {/* Quantity Controls */}
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center gap-1 border border-gray-300 rounded-lg p-1">
                        <button
                          type="button"
                          onClick={() => updateQuantity(item.id, Math.max(step, item.quantity - step))}
                          className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded transition"
                          data-testid={`decrease-quantity-${item.sku}`}
                          aria-label="Decrease quantity"
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                        <input
                          type="number"
                          inputMode="numeric"
                          min={step}
                          max={item.stock || 999}
                          step={step}
                          value={item.quantity}
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (raw === '') return;
                            const parsed = parseFloat(raw);
                            if (Number.isNaN(parsed) || parsed < step) return;
                            const max = item.stock || 999;
                            updateQuantity(item.id, Math.min(max, parsed));
                          }}
                          onBlur={(e) => {
                            const v = parseFloat(e.target.value);
                            const max = item.stock || 999;
                            if (!v || v < step) { updateQuantity(item.id, step); return; }
                            if (v > max) { updateQuantity(item.id, max); return; }
                            if (v % step !== 0) updateQuantity(item.id, Math.floor(v / step) * step);
                          }}
                          className="w-16 h-8 text-center font-semibold border-0 focus:outline-none focus:ring-2 focus:ring-orange-500 rounded"
                          data-testid={`quantity-input-${item.sku}`}
                        />
                        <button
                          type="button"
                          onClick={() => updateQuantity(item.id, item.quantity + step)}
                          className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded transition"
                          data-testid={`increase-quantity-${item.sku}`}
                          aria-label="Increase quantity"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                      <span className="text-xs text-gray-500 hidden sm:inline">{unitLabel}</span>

                      {/* Item Total */}
                      <div className="w-28 text-right">
                        <div className="text-lg font-bold" data-testid={`line-total-${item.sku}`}>{itemTotal.toFixed(2)} {currency}</div>
                        {itemDiscount > 0 && (
                          <div className="text-xs text-emerald-700" data-testid={`line-discount-${item.sku}`}>
                            −{itemDiscount.toFixed(2)} {currency}
                          </div>
                        )}
                      </div>

                      {/* Remove Button */}
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="text-red-500 hover:text-red-600 transition"
                        data-testid={`remove-item-${item.sku}`}
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Continue Shopping */}
            <div className="mt-6">
              <Link
                to="/catalog"
                className="inline-flex items-center space-x-2 text-orange-500 hover:text-orange-600"
              >
                <ArrowLeft className="h-5 w-5" />
                <span>{t('cart.continue_shopping')}</span>
              </Link>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-24">
              <h2 className="text-2xl font-bold mb-6" data-testid="order-summary">
                {t('checkout.order_summary')}
              </h2>

              {/* Coupon */}
              <div className="mb-5 pb-5 border-b border-gray-200">
                {coupon ? (
                  <div className="flex items-center justify-between rounded-lg border-2 border-orange-200 bg-orange-50 p-3" data-testid="active-coupon">
                    <div className="flex items-center gap-2">
                      <Tag className="h-4 w-4 text-orange-600" />
                      <div>
                        <div className="font-mono text-sm font-bold text-orange-700">{coupon.code}</div>
                        <div className="text-xs text-orange-600">−{coupon.discount_percent}%</div>
                      </div>
                    </div>
                    <button
                      onClick={removeCoupon}
                      className="text-orange-500 hover:text-red-500 transition"
                      data-testid="remove-coupon"
                      aria-label="Remove coupon"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleApply} className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {t('cart.coupon_label')}
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={couponInput}
                        onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                        placeholder={t('cart.coupon_placeholder')}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 uppercase"
                        data-testid="coupon-input"
                      />
                      <button
                        type="submit"
                        disabled={applying || !couponInput.trim()}
                        className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition disabled:bg-gray-300"
                        data-testid="apply-coupon-btn"
                      >
                        {applying ? '…' : t('cart.coupon_apply')}
                      </button>
                    </div>
                    {couponFeedback && (
                      <p
                        className={`text-xs ${couponFeedback.type === 'success' ? 'text-green-600' : 'text-red-600'}`}
                        data-testid="coupon-feedback"
                      >
                        {couponFeedback.message}
                      </p>
                    )}
                  </form>
                )}
              </div>

              <div className="space-y-3 mb-6">
                {(() => {
                  const subtotal = getSubtotal();
                  const bulkDisc = getBulkDiscount();
                  const baseTotal = subtotal + bulkDisc;
                  return (
                    <>
                      <div className="flex justify-between text-base">
                        <span>{t('cart.subtotal')}:</span>
                        <span className="font-semibold" data-testid="cart-subtotal">
                          {baseTotal.toFixed(2)} {currency}
                        </span>
                      </div>
                      {bulkDisc > 0 && (
                        <div className="flex justify-between text-base text-emerald-700" data-testid="bulk-discount-row">
                          <span>{t('cart.bulk_discount', 'Veľkoobchodná zľava')}:</span>
                          <span className="font-semibold">−{bulkDisc.toFixed(2)} {currency}</span>
                        </div>
                      )}
                    </>
                  );
                })()}
                {coupon && getCouponDiscount() > 0 && (
                  <div className="flex justify-between text-base text-orange-600" data-testid="coupon-discount-row">
                    <span>{t('cart.discount')} (−{coupon.discount_percent}%):</span>
                    <span className="font-semibold">
                      −{getCouponDiscount().toFixed(2)} {currency}
                    </span>
                  </div>
                )}
                <div className="border-t border-gray-200 pt-3">
                  <div className="flex justify-between text-xl font-bold">
                    <span>{t('cart.total')}:</span>
                    <span className="text-orange-500" data-testid="cart-total">
                      {getTotal().toFixed(2)} {currency}
                    </span>
                  </div>
                </div>
              </div>

              <Link
                to="/checkout"
                className="w-full block text-center px-6 py-3 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition mb-3"
                data-testid="proceed-checkout"
              >
                {t('cart.checkout')}
              </Link>

              <button
                onClick={clearCart}
                className="w-full px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                data-testid="clear-cart"
              >
                {t('cart.clear')}
              </button>
            </div>
          </div>
        </div>

        {/* Trust strip — reassurance below the summary */}
        <div className="mt-8">
          <TrustBadges variant="row" />
        </div>
      </div>
    </div>
  );
};

export default CartPage;